import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  browserLocalPersistence,
  setPersistence,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { mfaService } from "@shared/services/mfa.service";
import {
  AppUser,
  CustomUserFields,
  SubscriptionTier,
  ProfileVisibility,
} from "@/types/user";
import { createUser, getUser } from "@/services/users.service";
import { auth } from "@/firebaseConfig";
import { sendVerificationEmail } from "@/services/email.service";
import { trackUserJoin, trackUserLogin } from "@/services/analytics.service";
import { userActivityService } from "@/services/userActivity.service";
import { clearAllBrowserData } from "@/utils/cacheManager";
import { rateLimitingService } from "@shared/services/rateLimiting.service";
import { OptimizedSessionAuthService } from "@shared/services/optimizedSessionAuth.service";
import { DevAuthSyncService } from "@shared/services/devAuthSync.service";
import { AppTransferService } from "@shared/services/appTransfer.service";

// SECURITY: Dispatch cache management events for mobile cache service
const dispatchUserLoginEvent = (userId: string) => {
  window.dispatchEvent(
    new CustomEvent("userLogin", {
      detail: { userId },
    })
  );
};

const dispatchUserLogoutEvent = () => {
  window.dispatchEvent(new CustomEvent("userLogout"));
};

// Make sure Firebase Auth uses local persistence
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Auth persistence error:", error);
});

// Update the type definition to allow null values.
type AuthContextData = {
  user: AppUser | null;
  loadingAuth: boolean;
  setUser: React.Dispatch<React.SetStateAction<AppUser | null>>;
  login: typeof login;
  logout: typeof logout;
  googleSignIn: typeof googleSignIn;
  join: typeof join;
};

const userAuthContext = createContext<AuthContextData | undefined>(undefined);

export function useUserAuth() {
  const context = useContext(userAuthContext);
  if (!context) {
    throw new Error("useUserAuth must be used within an AuthProvider");
  }
  return context;
}

// Login function with rate limiting protection
const login = async (email: string, password: string) => {
  // Check rate limiting before attempting login
  const rateLimitStatus = rateLimitingService.isRateLimited("login", email);
  if (rateLimitStatus.isLimited) {
    const remainingTime = rateLimitStatus.blockUntil
      ? rateLimitStatus.blockUntil - Date.now()
      : 0;
    const minutes = Math.ceil(remainingTime / 60000);
    return {
      error: `Too many login attempts. Please try again in ${minutes} minute${
        minutes !== 1 ? "s" : ""
      }.`,
    };
  }

  // Apply progressive delay if configured
  const delay = rateLimitingService.getProgressiveDelay("login", email);
  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  try {
    // Authenticate with Firebase
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Check if email is verified (only for email/password users)
    // Google users are automatically considered verified
    const isGoogleUser = user.providerData.some(
      (provider) => provider.providerId === "google.com"
    );

    if (!isGoogleUser && !user.emailVerified) {
      // Email is not verified - redirect to verification page
      return {
        success: false,
        requiresVerification: true,
        user,
        error:
          "Please verify your email address to continue. Check your inbox for a verification link.",
      };
    }

    // Track login activity (non-blocking) - only for verified users
    try {
      await Promise.all([
        trackUserLogin(user.uid),
        userActivityService.trackActivity(user.uid, "login"),
      ]);
    } catch (trackError) {
    }

    // Record successful login (clears rate limiting)
    rateLimitingService.recordSuccessfulAttempt("login", email);

    return { success: true, user };
  } catch (error: any) {
    // Record failed attempt for rate limiting
    rateLimitingService.recordFailedAttempt("login", email);

    // Handle MFA required error
    if (error.code === "auth/multi-factor-auth-required") {
      const resolver = mfaService.getMFAResolver(error);
      return {
        success: false,
        requiresMFA: true,
        mfaResolver: resolver,
        error: "Two-factor authentication is required to complete sign-in.",
      };
    } else if (error.code === "auth/invalid-credential") {
      return { error: "Invalid email or password." };
    } else if (error.code === "auth/user-not-found") {
      return { error: "No account found with this email." };
    } else if (error.code === "auth/wrong-password") {
      return { error: "Incorrect password." };
    } else if (error.code === "auth/too-many-requests") {
      return {
        error:
          "Too many unsuccessful login attempts. Please try again later or reset your password.",
      };
    } else if (
      error.code === "auth/network-request-failed" ||
      error.code === "auth/timeout"
    ) {
      return {
        error: "Network error. Please check your connection and try again.",
      };
    } else {
      return { error: "Login failed. Please try again." };
    }
  }
};

// Join function for approved users (no invite codes required)
const join = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string
) => {
  // Check rate limiting before attempting signup
  const rateLimitStatus = rateLimitingService.isRateLimited("signup", email);
  if (rateLimitStatus.isLimited) {
    const remainingTime = rateLimitStatus.blockUntil
      ? rateLimitStatus.blockUntil - Date.now()
      : 0;
    const minutes = Math.ceil(remainingTime / 60000);
    return {
      error: `Too many signup attempts. Please try again in ${minutes} minute${
        minutes !== 1 ? "s" : ""
      }.`,
    };
  }

  // Apply progressive delay if configured
  const delay = rateLimitingService.getProgressiveDelay("signup", email);
  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  try {
    // Sanitize firstName and lastName to prevent security issues
    const sanitizedFirstName = firstName.trim();
    const sanitizedLastName = lastName.trim();

    // Validate inputs
    if (!email || !password || !sanitizedFirstName || !sanitizedLastName) {
      return { error: "All fields are required." };
    }

    // Create Firebase user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Create custom fields for the user
    const customFields: CustomUserFields = {
      userId: user.uid,
      email: user.email!,
      firstName: sanitizedFirstName,
      lastName: sanitizedLastName,
      handle: "",
      location: "",
      affiliations: null,
      profilePicture: user.photoURL,
      bio: null,
      dateOfBirth: null,
      joinedDate: new Date(user.metadata.creationTime!),
      followersCount: 0,
      followingCount: 0,
      postsCount: 0,
      likesGiven: 0,
      commentsCount: 0,
      communitiesJoined: [],
      language: "en",
      theme: "light",
      notificationsEnabled: true,
      interests: [],
      isProfilePrivate: false, // Default to visible in directory (users can make private later)
      blockedUsers: [],
      accountStatus: "active",
      lastLogin: new Date(),
      verified: false,
      passwordResetRequired: false,
      hasCompletedOnboarding: false,
      agreedToTermsAt: null,
      agreedToGuidelinesAt: null,
      emailVerified: false,
      phoneNumber: user.phoneNumber,
      secondaryEmail: null,
      lastPostDate: null,
      recentSearches: [],
      devices: [],
      loginMetrics: 0,
      viewsMetrics: 0,
      likesMetrics: 0,
      commentsMetrics: 0,
      subscription: SubscriptionTier.Basic,
      subscriptionStartDate: new Date(),
      subscriptionEndDate: null,
      jobTitle: "",
      quote: "",
      isStaff: false,
      isCommunityAdmin: [],
      institutionalAdmin: [],
      isTrustee: [],
      pausedUntil: null,
      profileVisibility: ProfileVisibility.Public,
      directoryVisibility: ProfileVisibility.Public,
      affiliationsVisibilitySettings: [],
      agreedToPrivacyAt: null,
      mainAppTOSAcknowledgedAt: null,
      mainAppPrivacyAcknowledgedAt: null,
      mainAppGuidelinesAcknowledgedAt: null,
      whitelabelTOSAcknowledgedAt: null,
      whitelabelPrivacyAcknowledgedAt: null,
      whitelabelGuidelinesAcknowledgedAt: null,
      lastLegalDocumentVersion: null,
    };

    // Create user document
    await createUser(user.uid, customFields);

    // Track user join event
    try {
      await trackUserJoin(user.uid);
    } catch (error) {
      console.error("Failed to track user join:", error);
      // Non-blocking - continue even if tracking fails
    }

    // Send verification email
    const emailResult = await sendVerificationEmail(user);
    if (emailResult.error) {
      console.error("Failed to send verification email:", emailResult.error);
      // We still return success since the user was created, but log the email error
    }

    // Record successful signup (clears rate limiting)
    rateLimitingService.recordSuccessfulAttempt("signup", email);

    return {
      success: true,
      user,
      requiresVerification: true, // Email/password users need to verify
    };
  } catch (error: any) {
    // Record failed attempt for rate limiting
    rateLimitingService.recordFailedAttempt("signup", email);

    console.error("Error during join:", error);

    // Handle specific Firebase auth errors
    if (error.code === "auth/email-already-in-use") {
      return {
        error:
          "This email is already registered. Please use a different email or sign in.",
      };
    } else if (error.code === "auth/weak-password") {
      return {
        error: "Password is too weak. Please choose a stronger password.",
      };
    } else if (error.code === "auth/invalid-email") {
      return { error: "Invalid email address format." };
    }

    return { error: "Signup failed. Please try again." };
  }
};

// Logout function
const logout = async () => {
  try {
    // Use optimized session auth service to sign out (clears cookies too)
    await OptimizedSessionAuthService.signOut();

    // Clear all cached data to prevent stale data issues on next login
    try {
      await clearAllBrowserData();
    } catch (cacheError) {
      // Don't fail logout if cache clearing fails
    }

    return { success: true };
  } catch (error) {
    console.error("❌ [Logout] Error during logout:", error);
    return { error: "Logout failed. Please try again." };
  }
};

// Safely handle social/secondary auth providers
const googleSignIn = async () => {
  // Use a generic identifier for Google sign-in rate limiting
  const userIdentifier =
    "google_signin_" + new Date().toISOString().split("T")[0]; // Per day

  // Check rate limiting before attempting Google sign-in
  const rateLimitStatus = rateLimitingService.isRateLimited(
    "googleSignIn",
    userIdentifier
  );
  if (rateLimitStatus.isLimited) {
    const remainingTime = rateLimitStatus.blockUntil
      ? rateLimitStatus.blockUntil - Date.now()
      : 0;
    const minutes = Math.ceil(remainingTime / 60000);
    return {
      error: `Too many sign-in attempts. Please wait ${minutes} minute${
        minutes !== 1 ? "s" : ""
      } before trying again.`,
    };
  }

  try {
    // Create a basic Google Auth Provider
    const googleAuthProvider = new GoogleAuthProvider();

    // Add minimal required scopes
    googleAuthProvider.addScope("profile");
    googleAuthProvider.addScope("email");

    // Use simple configuration
    googleAuthProvider.setCustomParameters({
      prompt: "select_account",
    });

    // Use signInWithPopup for a better UX
    const result = await signInWithPopup(auth, googleAuthProvider);
    const firebaseUser = result.user;

    // Check if the user already exists in Firestore
    const existingUser = await getUser(firebaseUser.uid);
    if (!existingUser) {
      // New Google user - they should go through the signup verification process
      // Delete the Firebase account and direct them to signup
      await firebaseUser.delete();
      return {
        error:
          "To join this institution, please start with our verification process using the signup option.",
        newUser: true,
      };
    }

    // Existing user - proceed with login
    // Track login activity (non-blocking)
    try {
      await Promise.all([
        trackUserLogin(firebaseUser.uid),
        userActivityService.trackActivity(firebaseUser.uid, "login"),
      ]);
    } catch (trackError) {
    }

    // Record successful login (clears rate limiting)
    rateLimitingService.recordSuccessfulAttempt("googleSignIn", userIdentifier);

    return { success: true, user: firebaseUser };
  } catch (error: any) {
    // Record failed attempt for rate limiting
    rateLimitingService.recordFailedAttempt("googleSignIn", userIdentifier);

    // Handle specific Google Auth errors with user-friendly messages
    if (error.code === "auth/popup-closed-by-user") {
      return {
        error: "Sign-in was cancelled. Please try again when ready.",
      };
    }
    if (error.code === "auth/popup-blocked") {
      return {
        error:
          "Pop-up was blocked by your browser. Please allow pop-ups and try again.",
      };
    }
    if (error.code === "auth/network-request-failed") {
      return {
        error: "Network error. Please check your connection and try again.",
      };
    }
    if (error.code === "auth/account-exists-with-different-credential") {
      return {
        error:
          "This email is already registered with a different sign-in method. Please use your original sign-in method.",
        conflictEmail: error.email,
      };
    }
    if (error.code === "auth/too-many-requests") {
      return {
        error: "Too many attempts. Please wait a moment and try again.",
      };
    }

    return {
      error:
        "Sign-in failed. Please try again or contact support if the problem persists.",
    };
  }
};

// Interface for the UserAuthProvider component
interface IUserAuthProviderProps {
  children: React.ReactNode;
}

export const UserAuthProvider: React.FC<IUserAuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loadingAuth, setloadingAuth] = useState<boolean>(true);
  const [lastUserUpdate, setLastUserUpdate] = useState<number>(0);

  // Simple helper to get user data when auth is ready
  const getUserDataWhenReady = useCallback(
    async (firebaseUser: any): Promise<CustomUserFields | null> => {
      try {
        // Wait for Firebase Auth token to be ready
        await firebaseUser.getIdToken();
        return await getUser(firebaseUser.uid);
      } catch (error: any) {
        console.error("Error fetching user data:", error);
        return null;
      }
    },
    []
  );

  // Memoize user creation to prevent unnecessary re-renders with stable references
  const createUserObject = useCallback(
    (firebaseUser: any, userData: CustomUserFields | null) => {
      // Determine if user is verified based on Firebase and provider
      const isGoogleUser = firebaseUser.providerData.some(
        (provider: any) => provider.providerId === "google.com"
      );
      const isVerified = isGoogleUser || firebaseUser.emailVerified;

      if (userData) {
        // Create stable user object with consistent property values
        const userObject = {
          ...firebaseUser,
          ...userData,
          isVerified, // Use Firebase verification status
          // Ensure these properties are always defined to prevent undefined comparisons
          hasCompletedOnboarding: userData.hasCompletedOnboarding || false,
          agreedToTermsAt: userData.agreedToTermsAt || null,
          agreedToGuidelinesAt: userData.agreedToGuidelinesAt || null,
          accountStatus: userData.accountStatus || "active",
          verified: userData.verified || false,
        } as AppUser;

        return userObject;
      } else {
        return {
          ...firebaseUser,
          isVerified, // Use Firebase verification status even for incomplete profiles
          hasCompletedOnboarding: false,
          agreedToTermsAt: null,
          agreedToGuidelinesAt: null,
          accountStatus: "active",
          verified: false,
        } as AppUser;
      }
    },
    []
  );

  useEffect(() => {
    let sessionMonitorCleanup: (() => void) | null = null;
    let devAuthSyncCleanup: (() => void) | null = null;

    // Start development auth sync in localhost
    const isLocalhost = window.location.hostname.includes("localhost");
    if (isLocalhost) {
      devAuthSyncCleanup = DevAuthSyncService.startListening();
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setloadingAuth(true);

      if (firebaseUser) {
        // Create session for cross-subdomain auth (only once per sign-in)
        try {
          await OptimizedSessionAuthService.createSession();
        } catch (error) {
          // Session creation failed silently
        }

        // Start monitoring session state for cross-subdomain sign-out
        if (!sessionMonitorCleanup) {
          sessionMonitorCleanup =
            OptimizedSessionAuthService.monitorSessionState(() => {
              // User will be signed out automatically by the monitor
            });
        }
        // Only update user data periodically OR if this is the first load
        const now = Date.now();
        const shouldUpdate = now - lastUserUpdate > 30000 || !user; // 30 seconds instead of 10

        if (shouldUpdate) {
          try {
            const userData = await getUserDataWhenReady(firebaseUser);
            const newUser = createUserObject(firebaseUser, userData);

            // Presence stamp (same as main app): lastLogin was historically written only
            // at account creation, so "last active" data went stale forever. Best-effort,
            // throttled to ~1h so periodic auth refreshes don't burn writes.
            try {
              const prevRaw =
                (userData as any)?.lastActivity || (userData as any)?.lastLogin;
              const prev =
                typeof prevRaw?.toDate === "function"
                  ? prevRaw.toDate()
                  : prevRaw
                  ? new Date(prevRaw)
                  : null;
              if (
                userData &&
                (!prev || isNaN(prev.getTime()) || Date.now() - prev.getTime() > 60 * 60 * 1000)
              ) {
                const { getFirestore, doc, updateDoc, serverTimestamp } =
                  await import("firebase/firestore");
                updateDoc(doc(getFirestore(), "users", firebaseUser.uid), {
                  lastLogin: serverTimestamp(),
                  lastActivity: serverTimestamp(),
                }).catch(() => {});
              }
            } catch {
              // Never block auth on presence stamping.
            }

            // Only update if critical navigation properties changed
            setUser((prevUser) => {
              if (
                !prevUser ||
                prevUser.uid !== newUser.uid ||
                prevUser.emailVerified !== newUser.emailVerified ||
                prevUser.hasCompletedOnboarding !==
                  newUser.hasCompletedOnboarding
              ) {
                setLastUserUpdate(now);
                return newUser;
              }
              return prevUser; // Keep the same object reference
            });

            // Only dispatch events on actual user changes
            if (!user || user.uid !== firebaseUser.uid) {
              dispatchUserLoginEvent(firebaseUser.uid);

              try {
                await userActivityService.trackActivity(
                  firebaseUser.uid,
                  "app_open"
                );
              } catch (error) {
                // Failed to track app open silently
              }
            }
          } catch (error) {
            // Error in auth state change handler
          }
        }
      } else {
        setUser(null);
        setLastUserUpdate(0);
        dispatchUserLogoutEvent();

        // Clean up session monitoring when signed out
        if (sessionMonitorCleanup) {
          sessionMonitorCleanup();
          sessionMonitorCleanup = null;
        }
      }

      setloadingAuth(false);
    });

    // Check for existing session on app initialization (throttled)
    const initializeSession = async () => {
      if (!auth.currentUser) {
        try {
          // First check if this is a transfer (development uses localStorage)
          if (AppTransferService.isTransferPage()) {
            const transferResult =
              await AppTransferService.handleIncomingTransfer();
            if (transferResult.success) {
              return; // Don't do session check if transfer was successful
            } else {
              // Only force session check if transfer failed
              await OptimizedSessionAuthService.forceSessionCheck();
            }
          } else {
            // For direct access to whitelabel, don't force session checks
            // Let Firebase Auth handle the authentication state naturally
          }
        } catch (error) {
          // Handle errors gracefully
        }
      } else {
      }
    };

    initializeSession();

    // Force session check when window gains focus (for transfers and logout detection)
    const handleFocus = async () => {
      try {
        if (!auth.currentUser) {
          // Check for new sessions if not logged in
          await OptimizedSessionAuthService.forceSessionCheck();
        } else {
          // Check if current session is still valid (detect cross-domain logouts)
          const sessionResult =
            await OptimizedSessionAuthService.checkAuthStatus();
          if (!sessionResult.success) {
            await firebaseSignOut(auth);
          } else {
          }
        }
      } catch (error) {
        // Focus session check failed - don't sign out on network errors
      }
    };

    window.addEventListener("focus", handleFocus);

    // Also check when page becomes visible (handles tab switching)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && auth.currentUser) {
        try {
          // Quick session validation when tab becomes visible
          const sessionResult =
            await OptimizedSessionAuthService.checkAuthStatus();
          if (!sessionResult.success) {
            await firebaseSignOut(auth);
          } else {
          }
        } catch (error) {
          // Visibility session check failed - don't sign out on network errors
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (sessionMonitorCleanup) {
        sessionMonitorCleanup();
      }
      if (devAuthSyncCleanup) {
        devAuthSyncCleanup();
      }
    };
  }, [getUserDataWhenReady, createUserObject, user?.uid, lastUserUpdate]);

  const value: AuthContextData = useMemo(
    () => ({
      user,
      loadingAuth,
      setUser,
      login,
      logout,
      googleSignIn,
      join,
    }),
    [user, loadingAuth]
  );

  return (
    <userAuthContext.Provider value={value}>
      {children}
    </userAuthContext.Provider>
  );
};
